import { grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

// The ramp's own colours live in util/reportColors
export const NEUTRAL_COLOR = "#FFFFFF";

// Tendency columns — how a team divides its possessions/shots. These render
// as a plain value + its rank / percentile as a small grey number (GreyNumeral;
// the freq needle until 2026-10-10) — never the quality ramp, because a
// distribution mix is not good or bad (design/CONVENTIONS.md). Trans Rate and Avg Crashers are
// deliberately NOT here (user call 2026-08-24): the staff treats them as
// unambiguously good/bad style choices, so they keep the ramp chip.
export const FREQUENCY_HEADERS = new Set([
  "% of Poss",
  "% of Shots",
  // TR5 (2026-10-08): the P&R class tables share out actions, the start-type
  // tables valid chances (By action's rate is "Per 100", below)
  "% of Actions",
  "% of Chances",
  "Zone Rate",
  // screen-defender coverage tendencies — needle rotates by the league
  // percentile of the share (how drop/switch-heavy, not good/bad)
  "Drop%",
  "Switch%",
  // player action tables (Top Actions / Action Defenders / Closeouts /
  // 3PT-FT, 2026-09-10): volumes and mix rates are tendencies
  "Per 100",
  "Under%",
  "Roll%",
  "Pop%",
  "Short%",
  "Drive L%",
  "Drive R%",
  "No Drive%",
  // Drives table (TR4): direction shares, the direction's PPP rides as the
  // composite cell's subValue
  "Left%",
  "Right%",
  "Shot%",
  "3PA Share",
  "Dbl%",
  "Finish%",
  "3PA/100",
  "FTA/100",
]);

// Rate columns that read as one decimal ("4.3", not "4.30") regardless of
// chip treatment.
export const ONE_DECIMAL_HEADERS = new Set(
  Array.from(FREQUENCY_HEADERS).concat([
    "Trans Rate",
    "Avg Crashers",
    "Trans 3P Rate",
    "Trans 3P Shot Qual",
    "Trans 3P%",
  ])
);

// Season-table rows below this possession count are de-emphasized — ranks on
// tiny samples read as signal when they're noise. Game reports (percentile
// cells) are exempt: their percentiles are computed against a same-scope
// population where small samples are inherent.
export const SMALL_SAMPLE_POSS_THRESHOLD = 50;
// A game report's shot-chart rows (zones and 3PT detail) grey, and the backend withholds their chip, under 3 shots:
// at the season floors (25 TSA / 40 3PA) nearly every row of one game greyed out (Trevor, 2026-10-09). Matches
// shot_profiles.GAME_ZONE_MIN_TSA / GAME_BUCKET_MIN_3PA.
export const GAME_SHOT_SAMPLE_FLOOR = 3;
// A game report's breakdown rows under this many possessions (actions, chances) grey out and lose their percentile,
// with a note (Trevor, checkpoint 1 decision D; on the coaches' game report too since 2026-10-10).
export const GAME_SAMPLE_FLOOR = 5;

// Low-sample de-emphasis fades the whole row (milder than a 0-poss row).
export const LOW_SAMPLE_ROW_OPACITY = 0.5;

// Subtle zebra striping, applied per logical parent row (a parent and its
// sub-rows share one stripe).
export const ROW_STRIPE_COLOR = alpha(grey[500], 0.06);

// Chip tiering (user call 2026-08-24, CONVENTIONS "chips only on headline
// numbers"): the hybrid rank/percentile numeral appears ONLY on the anchor
// efficiency columns; secondary columns keep the ramp-colored chip with the
// value alone (rank/percentile in a hover tooltip). The 2nd-chance/transition
// margin is the reb/trans pages' first-class metric, so it carries the
// numeral too. Avg Crashers / ORB% carry it only on the reb tables — that's
// a per-table opt-in (extraNumeralHeaders), not this global set, because
// ORB% is also a four-factors column on every phase table.
export const HYBRID_NUMERAL_HEADERS = new Set([
  "PPP",
  "xPPP",
  "2nd Chance vs Transition Margin",
  // back-compat: pre-rename header still in stored 2025 GAME blobs
  "Net ORB - Trans PPP",
]);

// Reb tables' per-table numeral opt-in (user call 2026-09-10: visual ranks
// on the process metrics there).
export const REB_TABLE_NUMERAL_HEADERS = new Set(["Avg Crashers", "ORB%"]);

// Chance-level point-rate columns display per-chance (1.08, not 108.2) on the
// phase tables — the personnel/player-blob scale (user call 2026-08-24).
// Applied per-table (BasicTeamSectionPage opts in) because the "PPP" header is
// also used by the overview's possession-level PP100, which stays ×100.
// Over-Expected columns are differences and stay on the ×100 points scale.
// Pts/ORB and Pts/Miss are NOT here: the backend emits them per-unit already
// (frac-scaled ORBAPPP/ShotMissAPPP) — dividing again crushed them to
// 0.00/0.01 (bug, fixed 2026-09-10).
export const PER_CHANCE_PPP_HEADERS = new Set([
  "PPP",
  "xPPP",
  // the first-shot quadrants' overall row, per halfcourt possession before its first ORB (IV1, 2026-10-10)
  "First Shot PPP",
  "First Shot xPPP",
  "Sw PPP",
  "Trans PPP",
  "2nd Chance vs Transition Margin",
  // back-compat: pre-rename header still in stored 2025 GAME blobs
  "Net ORB - Trans PPP",
  // player action table PPP splits
  "PPP v Und",
  "PPP v Ovr",
  "PPP v Dbl",
  "Fin PPP",
  "Fin xPPP",
  "Shot PPP",
  "No-Shot PPP",
]);
// Chance PPP charges the next possession's transition value (gpts), so a single game's small sample can dip below
// zero; a points-per-chance reading can't, so the display floors at 0.00 (Trevor, 2026-10-10). The blob keeps the raw
// value, and its percentile ranks it. The margins are differences, legitimately negative: never floored.
export const SIGNED_PER_CHANCE_HEADERS = new Set([
  "2nd Chance vs Transition Margin",
  "Net ORB - Trans PPP",
]);

// The action tables' PPP / xPPP are per chance, not per action (TR5): a header
// tooltip says so on those tables only (ACTION_TABLE_SECTIONS + the player
// sub-table blocks).
export const ACTION_PPP_HEADER_TOOLTIPS: Record<string, string> = {
  PPP: "Points per chance in which this action occurred (valid chances; a chance with two of this action counts once)",
  xPPP: "Expected points per chance in which this action occurred (valid chances; a chance with two of this action counts once)",
};
// "Per 100" keeps its short header; the hover says what it is per (Trevor
// 2026-10-08): team halfcourt possessions on By action, team possessions in
// the games he played on the player tables.
export const BY_ACTION_HEADER_TOOLTIPS: Record<string, string> = {
  ...ACTION_PPP_HEADER_TOOLTIPS,
  "Per 100": "Actions per 100 team halfcourt possessions",
};
export const PLAYER_ACTION_HEADER_TOOLTIPS: Record<string, string> = {
  ...ACTION_PPP_HEADER_TOOLTIPS,
  "Per 100": "Actions per 100 team possessions in games he played",
};
// The action tables and their header hover text (the Top Actions / Action
// Defenders sub-tables take PLAYER_ACTION_HEADER_TOOLTIPS).
export const ACTION_TABLE_HEADER_TOOLTIPS: Record<
  string,
  Record<string, string>
> = {
  "By halfcourt offensive action": BY_ACTION_HEADER_TOOLTIPS,
  "PR Type": ACTION_PPP_HEADER_TOOLTIPS,
  "PR Partners": ACTION_PPP_HEADER_TOOLTIPS,
  "Sm-Sm Type": ACTION_PPP_HEADER_TOOLTIPS,
  "Sm-Sm Partners": ACTION_PPP_HEADER_TOOLTIPS,
  "PR Coverage": ACTION_PPP_HEADER_TOOLTIPS,
  "Sm-Sm Coverage": ACTION_PPP_HEADER_TOOLTIPS,
  "PR Screen Defenders": PLAYER_ACTION_HEADER_TOOLTIPS,
  "Drives by Season": {
    ...ACTION_PPP_HEADER_TOOLTIPS,
    Drives: "Halfcourt drives in the regular season, on any team he played for",
    "Per 100": "Drives per 100 possessions of his teams in the games he played",
  },
  "Sm-Sm Screen Defenders": PLAYER_ACTION_HEADER_TOOLTIPS,
  Closeouts: PLAYER_ACTION_HEADER_TOOLTIPS,
};

// Individual Rebounding's header hover text (RV1, Trevor 2026-10-16), per reb / trans half: "Misses" is the
// opponent's on the defensive half, his team's on the offensive one.
const REBOUNDING_SHARED_TOOLTIPS: Record<string, string> = {
  "Starting Expectation":
    "His effect on his team's share of the rebound when the shot goes up, from size and court position",
  "Team Impact":
    "His team's rebounding over expectation with him on the floor, accounting for the other nine players",
  Value:
    "Starting expectation + team impact: team rebounds per 100 misses above an average player",
};
const REBOUNDING_HEADER_TOOLTIPS: Record<"orb" | "drb", Record<string, string>> = {
  drb: {
    ...REBOUNDING_SHARED_TOOLTIPS,
    Misses: "Opponent's unfouled missed field goals while he was on the floor",
    "DRB (exp)":
      "His defensive rebounds, and the number expected when the shot went up",
    "ORB Allowed (exp)":
      "Offensive rebounds by the man he was matched up with when the shot went up, and the number expected from where that man stood, the shot, and whether he crashed",
    "ORB Allowed +/-":
      "His man's offensive rebounds compared to expected: positive means he gave up more than expected",
    "DRB Impact":
      "His share of the team's defensive rebounding: position allowed counts double against him, rebounds he wins beyond his chance count a fifth",
  },
  orb: {
    ...REBOUNDING_SHARED_TOOLTIPS,
    Misses: "His team's unfouled missed field goals while he was on the floor, his own included",
    Crashes: "Teammates' non-fouled threes he went to the glass on",
    "Crash Rate Over Positional Expectation":
      "His crash rate on teammates' non-fouled threes minus the rate expected for his position",
    "ORB (exp)":
      "His offensive rebounds, and the number expected when the shot went up",
    "ORB +/-": "His offensive rebounds compared to expected",
  },
};
// The rebounding value columns read in rebounds on a game, per 100 misses on a season
const REBOUNDING_SCALED_HEADERS = new Set([
  "ORB +/-",
  "ORB Allowed +/-",
  "DRB Impact",
  "Starting Expectation",
  "Team Impact",
]);
export const individualReboundingHeaderTooltips = (
  side: "orb" | "drb",
  singleGame: boolean
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(REBOUNDING_HEADER_TOOLTIPS[side]).map(([header, text]) => [
      header,
      REBOUNDING_SCALED_HEADERS.has(header)
        ? `${text} (${singleGame ? "in rebounds" : "per 100 misses"})`
        : text,
    ])
  );

// A rate whose cell carries its sample (`n`, e.g. the attempts behind the
// Off-Ball Screens 3P%) greys out with no chip under this floor (TR5, Trevor
// 2026-10-08; the backend scores the percentile vs the same 15+ cohort).
export const CELL_SAMPLE_FLOORS: Record<string, { min: number; unit: string }> =
  {
    "3P%": { min: 15, unit: "3PA" },
    // A game's transition threes (Trevor 2026-10-21: 2025-26 team-games average 6.2, 8% have under 3). Only game
    // cells carry `n`; Trans 3P Rate never does (its denominator is every transition shot, so 0 of 10 is real).
    "Trans 3P%": { min: 3, unit: "transition 3PA" },
    "Trans 3P Shot Qual": { min: 3, unit: "transition 3PA" },
  };

// Already-per-unit point rates: two decimals, no rescale.
export const TWO_DECIMAL_HEADERS = new Set(["Pts/ORB", "Pts/Miss"]);

// Rebounding value on Individual Rebounding (RV1, projects/rebound_value, Trevor 2026-10-16): explicitly signed
// (util/formatSigned). A game's are counts in rebounds (1 decimal), a season's rates per 100 misses (2 decimals).
// ORB Conceded Over Expectation is ORB Allowed +/-'s predecessor on blobs built before RV1, on the same scales.
export const REBOUND_VALUE_HEADERS = new Set([
  "ORB +/-",
  "ORB Allowed +/-",
  "DRB Impact",
  "Starting Expectation",
  "Team Impact",
  "Value",
  "ORB Conceded Over Expectation",
]);
// Crash rate over expectation, in percentage points: signed, 1 decimal on both scopes. RV1 renamed it; blobs built
// before carry "Crash Rate Over Expectation".
export const CRASH_OE_HEADERS = new Set([
  "Crash Rate Over Positional Expectation",
  "Crash Rate Over Expectation",
]);
// A game's rebounds beside the number expected when the shot went up (the cell's subValue): "4 (3.1)". Counts: no
// percentile.
export const EXPECTED_COUNT_HEADERS = new Set([
  "ORB (exp)",
  "DRB (exp)",
  "ORB Allowed (exp)",
]);

// Volume counts render as whole numbers. Game reports show the player tables'
// volume as a count (Actions, Closeouts, 3PA, FTA) where the season shows per
// 100. The action tables count Actions, the start-type tables Chances (TR5).
// Misses / Crashes: a game's Individual Rebounding sample and crash count (RV1).
export const COUNT_HEADERS = new Set([
  "Poss",
  "Actions",
  "Chances",
  "Closeouts",
  "3PA",
  "FTA",
  "Misses",
  "Crashes",
  // Drives by Season's count (Jacob 2026-10-30)
  "Drives",
]);

// The row's volume cell — what the low-sample fade and the single-game floor
// read: possessions, or the action / chance count a table shows instead (TR5).
export const VOLUME_HEADERS = ["Poss", "Actions", "Chances"] as const;

// Column groups: volume/rate | efficiency (PPP, xPPP) | four factors (TS%,
// ORB%, TOV%). A light divider is drawn before each group-starting column.
// Degrades gracefully on non-standard column sets (no matching header → no
// divider).
// Column groups (left divider before the first PRESENT header of each group):
// efficiency (PPP…) and the four-factors group. Shot Qual ("xTS%" in blobs
// built before TR3) belongs with the four-factors — when a table carries it,
// the border moves there; tables without it fall back to TS%.
export const COLUMN_GROUPS: string[][] = [["PPP"], ["Shot Qual", "xTS%", "TS%"]];
export const getColumnGroupStarts = (headers: string[]): Set<string> => {
  const starts = new Set<string>();
  for (const candidates of COLUMN_GROUPS) {
    const first = candidates.find((h) => headers.includes(h));
    if (first) starts.add(first);
  }
  return starts;
};
export const COLUMN_GROUP_BORDER = "1px solid #e0e0e0";

// By Type of Possession: Trans Rate only means something on possession starts
// a defense can actually run from — make, miss, AFT, live-ball TO. The
// non-runnable start rows (SOB/BOB/dead ball/…) render a blank Trans Rate
// cell. Keyed on the RAW row names the backend emits, and enumerated as a
// BLANK list (not an allow list) so rows outside the possession-type table —
// the overview Overall rows, notably — keep their Trans Rate cells.
export const TRANS_RATE_BLANK_ROWS = new Set([
  "db",
  "orb",
  "ato",
  "SOB",
  "BOB",
  "other",
]);

export const CONDENSED_FONT_FAMILY = "'Roboto Condensed', sans-serif";
// The report's hover card: its ink on white with a hairline, in the table face (web only: a tooltip never prints).
// The shot chart's shot hover and the tables' cell breakdowns (RV1) share it.
export const REPORT_HOVER_CARD_SX = {
  bgcolor: "#fff",
  color: "#111",
  border: "1px solid #cfccc8",
  borderRadius: "3px",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.75rem",
  lineHeight: 1.35,
  px: 1,
  py: 0.5,
  m: 0,
};
// The report display face (personnel --pr-disp stack): section titles, page
// bands, kickers. Tables stay Roboto Condensed.
export const DISPLAY_FONT_FAMILY =
  "'United Sans Cond', 'Oswald', Impact, sans-serif";
// The personnel report's accent red (kickers, report header).
export const REPORT_RED = "#cf0a2c";
export const POR_TEAM_ID = 1610612757;

export const PLAYER_PAGE_USE_ACTIONS_CHART = true;

// The rebounding / transition tables mix both teams' numbers (a team's offensive rebounding with the transition it
// allows, and the mirror), so the opponent's columns say "allowed" (Trevor, 2026-10-26; "Our" / "Their" from
// 2026-10-10 read wrong on another team's season report). Keyed by the page: `orb` = offensive rebounding +
// transition defense, `drb` = defensive rebounding + transition offense. An allowed head renders stacked
// (ShotQualLabel.stackedHead: "Trans Rate" over "allowed"), so a column is no wider than before.
const REB_TRANS_COLUMNS = ["ORB%", "Pts/ORB", "Pts/Miss"];
// Transition threes on the Overall rows (Jacob, Trevor 2026-10-21): the side that runs, like Trans Rate
export const TRANS_THREE_COLUMNS = ["Trans 3P Rate", "Trans 3P Shot Qual", "Trans 3P%"];
const TRANS_COLUMNS = ["Trans Rate", "Trans PPP", ...TRANS_THREE_COLUMNS];
// Hover text for the heads that need more than the name
export const REB_TRANS_HEAD_NOTES: Record<string, string> = {
  "Trans 3P Rate": "share of transition shot attempts that are threes",
  "Trans 3P Shot Qual": "expected 3P% from shot quality on transition threes",
  "Trans 3P%": "transition threes made / attempted",
};
/** Each page's columns: whether the report's team or its opponents did it ("allowed"). */
export const REB_TRANS_ALLOWED: Record<"orb" | "drb", Record<string, boolean>> = {
  orb: Object.fromEntries([...REB_TRANS_COLUMNS.map((c) => [c, false]), ...TRANS_COLUMNS.map((c) => [c, true])]),
  drb: Object.fromEntries([...REB_TRANS_COLUMNS.map((c) => [c, true]), ...TRANS_COLUMNS.map((c) => [c, false])]),
};
